/* src/views/admin/kitchen/KitchenBoard.jsx */
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { jwtDecode } from 'jwt-decode';
import { getKitchenOrdersService } from '@/services/OrderServices';
import { setBranchId } from '@/security/Axios';

import ItemBoard from './components/ItemBoard';
import OrdersSection from './components/OrdersSection';
import Toolbar from './components/Toolbar';
import { bucketOf } from '@/utils/orderBuckets';

const POLL_INTERVAL = 10_000; // 10 s auto-refresh

function branchFromToken() {
  try {
    const tok = localStorage.getItem('jwt_token');
    if (!tok) return null;
    const decoded = jwtDecode(tok) || {};
    return decoded?.branch_id || null;
  } catch {
    return null;
  }
}

export default function KitchenBoard() {
  const dispatch = useDispatch();

  /* ───────── auth/tenant context ───────── */
  const authData      = useSelector((s) => s.Auth?.data?.data || {});
  const isSuperAdmin  = !!authData?.isSuperAdmin;
  const permissions   = authData?.permissions || [];
  const userBranchId  = authData?.branch_id || branchFromToken(); // fallback to JWT

  const canViewAll    = permissions.includes('view_all_branches');
  const canSwitch     = isSuperAdmin || canViewAll || permissions.includes('switch_branch');

  /* ───────── data state ───────── */
  const [orders, setOrders] = useState([]);

  /* ───────── UI state ───────── */
  const [expanded, setExpanded] = useState(null); // open card id
  const [activeTab, setActiveTab] = useState('ALL');
  const [search, setSearch] = useState('');

  const pollRef = useRef(null);
  const retried403Ref = useRef(false);

  /* ───────── harden branch header before any fetch ───────── */
  useEffect(() => {
    const lsBranch = localStorage.getItem('branch_id');

    if (!canSwitch) {
      // Non-switchable users must not use 'all' or a foreign branch.
      const safe = userBranchId ? String(userBranchId) : null;

      if (lsBranch === 'all') {
        // clear 'all' completely → backend will use JWT branch
        localStorage.removeItem('branch_id');
        localStorage.removeItem('branch_name');
        setBranchId(null); // removes header
      }

      // If LS/header has a different branch than the user’s default, fix it.
      if (safe && lsBranch && lsBranch !== safe) {
        localStorage.setItem('branch_id', safe);
        setBranchId(safe);
      }

      // If nothing set at all, remove header so token branch is used
      if (!lsBranch && !safe) {
        setBranchId(null);
      }
    }
  }, [canSwitch, userBranchId]);

  /* ───────── fetch orders (with one 403 recovery) ───────── */
  const fetchOrders = useCallback(async () => {
    try {
      const { data: resp } = await dispatch(
        getKitchenOrdersService({ mode: 'ALL' }) // kitchen-only endpoint
      );
      setOrders(resp?.data?.data ?? []);
      retried403Ref.current = false; // reset on success
    } catch (e) {
      const status = e?.statusCode || e?.response?.status;
      const msg = e?.message || e?.response?.data?.message || '';

      // If backend blocks branch switching, fix header once & retry
      if (status === 403 && /not allowed to switch branch/i.test(msg) && !retried403Ref.current) {
        retried403Ref.current = true;

        const safe = userBranchId ? String(userBranchId) : null;
        if (safe) {
          // force user branch
          localStorage.setItem('branch_id', safe);
          setBranchId(safe);
        } else {
          // or let backend fall back to JWT branch
          localStorage.removeItem('branch_id');
          localStorage.removeItem('branch_name');
          setBranchId(null);
        }

        try {
          const { data: r2 } = await dispatch(getKitchenOrdersService({ mode: 'ALL' }));
          setOrders(r2?.data?.data ?? []);
          return;
        } catch {
          // swallow; UI stays with previous data
        }
      }

      if (process.env.NODE_ENV !== 'production') {
        // eslint-disable-next-line no-console
        console.warn('[KitchenBoard] fetch failed:', { status, msg, e });
      }
    }
  }, [dispatch, userBranchId]);

  useEffect(() => {
    fetchOrders();
    pollRef.current = setInterval(fetchOrders, POLL_INTERVAL);
    return () => clearInterval(pollRef.current);
  }, [fetchOrders]);

  /* ───────── filters ───────── */
  const byTab = (o) => activeTab === 'ALL' || bucketOf(o.statusName) === activeTab;
  const bySearch = (o) =>
    !search || o.order_serial_no?.toLowerCase().includes(search.toLowerCase());

  const current  = orders.filter((o) => byTab(o) && bySearch(o));
  const dineIn   = current.filter((o) => o.order_type === 2);
  const takeAway = current.filter((o) => o.order_type === 1);

  /* ───────── aggregate sidebar items ───────── */
  const itemSummary = useMemo(() => {
    const map = new Map();
    current.forEach((o) => {
      o.order_items?.forEach((it) => {
        const key = `${it.item_id?._id ?? it.item_id}_${it.size ?? ''}`;
        const name = it.item_id?.name ?? 'Unknown item';
        if (!map.has(key)) map.set(key, { key, name, size: it.size ?? '', qty: 0 });
        map.get(key).qty += it.quantity ?? 0;
      });
    });
    return Array.from(map.values()).sort((a, b) => b.qty - a.qty);
  }, [current]);

  /* ───────── layout ───────── */
  return (
    <div className="bg-[#F5F6FB] min-h-screen pt-6 pb-6 px-6 flex gap-6 overflow-hidden">
      {/* Items board */}
      <ItemBoard items={itemSummary} />

      {/* Main board */}
      <div className="flex-1 flex flex-col overflow-hidden">
        <Toolbar
          activeTab={activeTab}
          onTabChange={setActiveTab}
          search={search}
          onSearch={setSearch}
        />

        <OrdersSection
          dineIn={dineIn}
          takeAway={takeAway}
          expanded={expanded}
          onToggle={setExpanded}
          onStatusUpdated={fetchOrders}
          activeTab={activeTab}
        />
      </div>
    </div>
  );
}
